import {
  DEFAULT_AROUND_RADIUS_IN_METERS,
  SortOrder
} from '@/domain/search/constants'
import { algoliaIndex } from './algolia'
import { FILTER_RESULTS_PER_PAGE } from '@/domain/filterOptions/constants'
import removeEmptyKeysAndValues from '@/utils/removeEmptyKeysAndValues'
import { isNonEmptyArray, isNonEmptyString } from '@/utils/checks'
import { baseSponsorFilters, baseStatusFilters, baseFilters } from './constants'

export type algoliaCenterSearchOptions = {
  filters?: string | string[]
  optionalFilters?: string[] | Array<string[]>
  ranking?: string[]
  hitsPerPage?: number
  aroundRadius?: number | string
  aroundPrecision?: number | string
  aroundLatLngViaIP?: boolean
  aroundLatLng?: string
}

/**
 * Performs a search using Algolia's search API with aroundLatLngViaIP, aroundLatLng, or without geo. Principal entry point for carousels.
 *
 * @param {Object} options - The search configuration options
 * @param {string} options.query - The search query string
 * @param {string|string[]} [options.filters=''] - Filtering criteria as a string or array of strings to be joined with 'AND'
 * @param {number} [options.hitsPerPage=FILTER_RESULTS_PER_PAGE] - Number of results per page
 * @param {number} [options.aroundRadius=DEFAULT_AROUND_RADIUS_IN_METERS] - Radius in meters for geo search
 * @param {number} [options.aroundPrecision] - radius in meters in which results are considered equidistant from the center point. Items further are banded together by multiple of this value.
 * @param {string[]|Array<string[]>} [options.optionalFilters] - Optional filtering criteria
 * @param {string[]} [options.ranking=[SortOrder.Custom]] - Search ranking parameters, defaults to custom without aroundLatLng, or Geo without aroundLatLngViaIP/aroundLatLng
 * @param {boolean} [options.aroundLatLngViaIP] - Whether to use the user's IP for geo-location, overrides aroundLatLng
 * @param {string} [options.aroundLatLng] - Geo coordinates for location-based search (format: "lat, lng")
 * @returns {Promise<{hits: Center[]}>} The search results from Algolia
 */
export const algoliaCenterSearch = ({
  query = '',
  filters = '',
  hitsPerPage = FILTER_RESULTS_PER_PAGE,
  aroundRadius = DEFAULT_AROUND_RADIUS_IN_METERS,
  aroundPrecision,
  optionalFilters,
  ranking,
  aroundLatLngViaIP,
  aroundLatLng
}: algoliaCenterSearchOptions & { query: string }) => {
  const configure: algoliaCenterSearchOptions = {
    filters,
    hitsPerPage,
    aroundRadius,
    aroundPrecision,
    optionalFilters,
    ranking,
    aroundLatLng
  }
  if (isNonEmptyString(aroundRadius) && !isNaN(parseInt(aroundRadius, 10))) {
    configure.aroundRadius = parseInt(aroundRadius)
  }
  if (
    isNonEmptyString(aroundPrecision) &&
    !isNaN(parseInt(aroundPrecision, 10))
  ) {
    configure.aroundPrecision = parseInt(aroundPrecision)
  }

  if (aroundLatLngViaIP && !aroundLatLng) {
    configure.aroundLatLngViaIP = true
    configure.ranking = [SortOrder.Distance, SortOrder.Custom]
  }
  if (isNonEmptyString(aroundLatLng)) {
    configure.aroundLatLng = aroundLatLng
    configure.aroundLatLngViaIP = false
    configure.ranking = []
  }

  if (isNonEmptyArray(filters)) {
    configure.filters = filters.join(' AND ')
  }

  if (!filters) {
    configure.filters = baseFilters
  }
  const cleanedConfigure = removeEmptyKeysAndValues(configure)

  return algoliaIndex.search(query, cleanedConfigure)
}

export default algoliaIndex
